import type { InfiniteData } from '@tanstack/react-query'
import type { Action, MessagePage, MessageSummary } from '../api/types'

export type MessagePages = InfiniteData<MessagePage, unknown>

/** Maps every cached message; return null to drop it from the list. */
export function patchPages(data: MessagePages | undefined, fn: (m: MessageSummary) => MessageSummary | null): MessagePages | undefined {
  if (!data) return data
  return { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.flatMap((m) => { const r = fn(m); return r ? [r] : [] }) })) }
}

export const REMOVES_FROM_LIST: ReadonlySet<Action> = new Set(['archive', 'trash', 'spam', 'notspam', 'inbox', 'move', 'delete'])

/** Optimistic effect of an action on one cached message (null = leaves the current list). */
export function applyOptimistic(m: MessageSummary, action: Action): MessageSummary | null {
  switch (action) {
    case 'read': return { ...m, seen: true }
    case 'unread': return { ...m, seen: false }
    case 'star': return { ...m, starred: true }
    case 'unstar': return { ...m, starred: false }
    default: return null
  }
}

export const INVERSE: Partial<Record<Action, Action>> = { read: 'unread', unread: 'read', star: 'unstar', unstar: 'star' }

export function flatten(data: MessagePages | undefined): MessageSummary[] {
  return data?.pages.flatMap((p) => p.items) ?? []
}

export function formatDate(iso: string, now = new Date()): string {
  const d = new Date(iso)
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  return d.toLocaleDateString([], { year: '2-digit', month: 'numeric', day: 'numeric' })
}

export function senderName(m: Pick<MessageSummary, 'from'>): string {
  return m.from.name || m.from.address || '(unknown)'
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/** Label tree ordered depth-first, for the sidebar and pickers. */
export function labelTree<T extends { id: string; parentId: string | null; name: string }>(labels: T[]): Array<T & { depth: number; path: string }> {
  const out: Array<T & { depth: number; path: string }> = []
  const walk = (parent: string | null, depth: number, prefix: string) => {
    for (const l of labels.filter((x) => x.parentId === parent).sort((a, b) => a.name.localeCompare(b.name))) {
      const path = prefix ? `${prefix}/${l.name}` : l.name
      out.push({ ...l, depth, path })
      walk(l.id, depth + 1, path)
    }
  }
  walk(null, 0, '')
  return out
}

/** Heading for the day-group a message belongs to in the list: Today, Yesterday, This week, then months. */
export function dayGroup(iso: string, now = new Date()): string {
  const d = new Date(iso)
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((startOf(now) - startOf(d)) / 86_400_000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return 'Earlier this week'
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) return 'Earlier this month'
  return d.toLocaleDateString([], d.getFullYear() === now.getFullYear() ? { month: 'long' } : { month: 'long', year: 'numeric' })
}
